/**
 * BugReportButton — the global 🐞 in the top nav. Anyone can report a problem from anywhere.
 *
 * On submit it captures a screenshot of the current view (html2canvas, lazy-loaded so it never
 * weighs down the entry bundle) plus the rolling capture buffers (last ~10 page navigations, API
 * requests, and console errors), and POSTs them with the user's step-by-step description. The
 * backend stores the screenshot and emails the owner. The screenshot is best-effort — if it fails,
 * the report still goes with the description + context.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSubmitBugReport } from "../api/bugReports";
import { errorMessage } from "../api/client";
import { getCapture } from "../utils/bugCapture";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { TextArea } from "./TextField";
import { useToast } from "./Toast";

async function captureScreenshot(): Promise<string | null> {
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      // Cap the pixel ratio so the PNG stays well under the server's 5 MB limit on big/retina screens.
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
    });
    return canvas.toDataURL("image/png");
  } catch {
    return null; // best-effort — report still sends without the image
  }
}

export function BugReportButton() {
  const { t } = useTranslation();
  const toast = useToast();
  const submit = useSubmitBugReport();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const text = description.trim();
    if (!text) return;
    setBusy(true);
    try {
      const screenshot = await captureScreenshot();
      await submit.mutateAsync({
        page: window.location.href,
        description: text,
        browser_info: navigator.userAgent,
        state_snapshot: getCapture(),
        screenshot,
      });
      toast.success(t("bug.sent"));
      setOpen(false);
      setDescription("");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-label={t("bug.report")}
        title={t("bug.report")}
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">🐞</span>
      </button>

      <Modal
        open={open}
        onClose={() => (busy ? undefined : setOpen(false))}
        title={t("bug.title")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!description.trim()}
              onClick={() => void send()}
            >
              {t("bug.submit")}
            </Button>
          </>
        }
      >
        <p className="muted" style={{ marginTop: 0 }}>
          {t("bug.intro")}
        </p>
        <TextArea
          label={t("bug.describeLabel")}
          value={description}
          rows={7}
          placeholder={t("bug.placeholder")}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Modal>
    </>
  );
}
